import { config } from "../config";

/**
 * Direct-region OIDC resolution for the EU auth broker (EU data residency).
 *
 * okta-auth-js validates id_token.iss against a single configured issuer and has no
 * dual-issuer hook. So rather than routing the token through the broker (whose iss the
 * SDK would reject), we resolve the user's region FIRST, then build OktaAuth pointed
 * straight at that region's Okta. The user authenticates directly against the region, so
 * the token is minted AND validated by it: iss stays NA or EU, and the SDK's
 * single-issuer check passes because it IS configured with that issuer.
 *
 * Admin Center resolves the region two ways:
 *  - Silently, when Home links across with ?login_hint=<email> — an XHR to the broker's
 *    /resolve returns the region issuer with no UI (see resolveRegionFromLoginHint).
 *  - Via the broker's branded email page, when AC is opened cold with no hint.
 *
 * Everything here is flag-agnostic; gating on `enable_eu` happens at the call sites
 * (and via the `euEnabled` argument to resolveOktaConfig).
 */

/**
 * Transient pre-auth target: the issuer + client the user is CURRENTLY logging in
 * against, resolved from the broker for THIS login only. NOT a durable "which region is
 * this user" cache — the token's own `iss` is the source of truth once authenticated.
 * Session-scoped so it clears on tab close; cleared as soon as a token exists.
 */
export const PENDING_ISSUER_KEY = "broker:pending:issuer";
export const PENDING_CLIENT_ID_KEY = "broker:pending:clientId";

/**
 * Loop guards for the silent cross-app SSO probe. The probe marker is set BEFORE the
 * prompt=none redirect and cleared only once a token exists, so we never probe twice.
 * The broker marker ensures a failed probe presents the broker at most once.
 */
export const SILENT_PROBE_DONE_KEY = "broker:silent-probe-done";
export const BROKER_SHOWN_KEY = "broker:shown-after-probe";

/** login_hint stashed across the redirect chain, which drops the query string. */
export const LOGIN_HINT_KEY = "broker:login_hint";

/**
 * The route the user was trying to reach before the broker hop. The broker always
 * returns to /login-region, so without this a cold open of a deep link would land on
 * "/" after login. Plays the role setOriginalUri plays in the non-broker flow, which
 * cannot be used here because the hop happens before any OIDC redirect starts.
 */
export const RETURN_PATH_KEY = "broker:return_path";

/** okta-auth-js persists tokens under this sessionStorage key. */
export const OKTA_TOKEN_STORAGE_KEY = "okta-token-storage";

/** Deadline for the broker's region lookup — see resolveRegionFromLoginHint. */
const BROKER_RESOLVE_TIMEOUT_MS = 5000;

export interface OidcTarget {
    issuer: string;
    clientId: string;
}

/** The broker's region resolver: GET /resolve?login_hint=<email> -> { region, issuer }. */
export function brokerResolveUrl(loginHint: string): string {
    return `${config.BROKER_URL}/resolve?login_hint=${encodeURIComponent(loginHint)}`;
}

/** The broker's branded email page, which resolves the region interactively. */
export function brokerStartUrl(returnUri: string): string {
    return `${config.BROKER_URL}/login?return_uri=${encodeURIComponent(returnUri)}`;
}

/**
 * Region is determined by the ISSUER of the already-authenticated token — the single
 * source of truth — not by any cached region flag.
 */
export function getIssuerFromStoredToken(): string | null {
    try {
        const raw = window.sessionStorage.getItem(OKTA_TOKEN_STORAGE_KEY);
        if (!raw) return null;
        const store = JSON.parse(raw);
        return store?.accessToken?.claims?.iss ?? store?.idToken?.claims?.iss ?? null;
    } catch {
        return null;
    }
}

/**
 * Map an issuer to Admin Center's own per-region SPA client_id.
 *
 * NA needs no dedicated config: ISSUER/CLIENT_ID are already the NA issuer and a
 * public-PKCE SPA client with the same /callback redirect URI. So an NA user resolves to
 * exactly the pair the app uses with the flag off — turning enable_eu on is a no-op for
 * them. Only the EU side is new.
 */
export function clientIdForIssuer(issuer: string): string | undefined {
    if (config.OIDC_EU_ISSUER && issuer === config.OIDC_EU_ISSUER) return config.OIDC_EU_CLIENT_ID;
    if (config.ISSUER && issuer === config.ISSUER) return config.CLIENT_ID;
    return undefined;
}

/**
 * Resolve the OIDC config OktaAuth must use:
 *  1. If a token exists, use ITS issuer (source of truth) + the matching client.
 *  2. Else, if a pre-auth login is in flight, use the transient pending target.
 *  3. Else null -> caller falls back to the default issuer.
 */
export function getActiveOidcConfig(): OidcTarget | null {
    const tokenIss = getIssuerFromStoredToken();
    if (tokenIss) {
        const clientId = clientIdForIssuer(tokenIss);
        if (clientId) return { issuer: tokenIss, clientId };
    }
    const pendingIssuer = window.sessionStorage.getItem(PENDING_ISSUER_KEY);
    const pendingClientId = window.sessionStorage.getItem(PENDING_CLIENT_ID_KEY);
    return pendingIssuer && pendingClientId ? { issuer: pendingIssuer, clientId: pendingClientId } : null;
}

/** True once we can authenticate (token issuer known OR a pending target set). */
export function hasActiveRegion(): boolean {
    return getActiveOidcConfig() !== null;
}

/** Persist the transient pre-auth target resolved by the broker. */
export function setPendingRegion(issuer: string, clientId: string): void {
    window.sessionStorage.setItem(PENDING_ISSUER_KEY, issuer);
    window.sessionStorage.setItem(PENDING_CLIENT_ID_KEY, clientId);
}

/** Clear the transient pending target (call once a token exists). */
export function clearPendingIssuer(): void {
    window.sessionStorage.removeItem(PENDING_ISSUER_KEY);
    window.sessionStorage.removeItem(PENDING_CLIENT_ID_KEY);
}

/** Clear every transient login-flow marker. Call once a token exists. */
export function clearLoginFlowMarkers(): void {
    clearPendingIssuer();
    window.sessionStorage.removeItem(SILENT_PROBE_DONE_KEY);
    window.sessionStorage.removeItem(BROKER_SHOWN_KEY);
    window.sessionStorage.removeItem(LOGIN_HINT_KEY);
    window.sessionStorage.removeItem(RETURN_PATH_KEY);
}

/**
 * The route to land on after the broker hop completes. Defaults to "/" when nothing was
 * stashed. Always relative, so a tampered sessionStorage value cannot redirect off-site.
 */
export function getReturnPath(): string {
    const stashed = window.sessionStorage.getItem(RETURN_PATH_KEY);
    // Must be rooted: rejects "https://evil.com", "javascript:..." and bare relatives.
    if (!stashed || !stashed.startsWith("/")) return "/";
    try {
        // A leading-slash test alone is not enough. Browsers normalise "\" to "/" and
        // strip tab/LF/CR from URLs, so "/\evil.com" and "/<TAB>/evil.com" both resolve
        // to "//evil.com" — protocol-relative, i.e. off-site. Resolving against our own
        // origin and comparing applies the SAME normalisation the browser would, so the
        // value we hand to location.assign is the one that was checked.
        const url = new URL(stashed, window.location.origin);
        if (url.origin !== window.location.origin) return "/";
        // Re-check the string we are about to RETURN, not just the parsed input.
        // Collapsing dot-segments can yield a protocol-relative pathname even though
        // parsing stayed on our origin: "/.//evil.com" normalises to "//evil.com".
        // Re-resolving models exactly what location.assign will do with this value.
        const path = `${url.pathname}${url.search}${url.hash}`;
        if (new URL(path, window.location.origin).origin !== window.location.origin) return "/";
        return path;
    } catch {
        return "/";
    }
}

/**
 * Redirect to the broker's branded email page to resolve the region, remembering where
 * the user was headed so LoginRegion can send them back there after the hop.
 *
 * `returnPath` overrides the current URL, for callers that are NOT sitting on a real app
 * route. The OIDC callback is the case that matters: its URL carries the failed probe's
 * error, so stashing it would send LoginRegion straight back into the error handler.
 */
export function startRegionResolution(returnPath?: string): void {
    const currentPath = returnPath ?? `${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.sessionStorage.setItem(RETURN_PATH_KEY, currentPath);
    window.location.assign(brokerStartUrl(`${window.location.origin}/login-region`));
}

/**
 * Decide what a failed OIDC callback means and act on it. This is the redirect-loop
 * detection protocol for direct-region auth, kept out of the UI: the component that
 * okta-react renders as `errorComponent` only supplies the error and the pre-probe URI.
 *
 * `originalUri` is the route the user was on before the silent probe (recorded by
 * signIn via setOriginalUri). It must be passed in rather than read from the URL —
 * the current URL is the failed callback.
 */
export function handleCallbackError(error: Error, originalUri?: string): void {
    const msg = `${error?.message ?? ""} ${(error as unknown as { errorCode?: string })?.errorCode ?? ""}`;
    // The silent probe (prompt=none) returns login_required when the region has NO
    // active session. Present the broker — but only ONCE, guarded, so a subsequent
    // failure can't bounce probe<->broker forever.
    if (/login_required|interaction_required|consent_required/i.test(msg)) {
        const brokerShown = window.sessionStorage.getItem(BROKER_SHOWN_KEY) === "1";
        if (!brokerShown) {
            window.sessionStorage.setItem(BROKER_SHOWN_KEY, "1");
            // Keep the probe marker set: after the broker resolves the region and
            // returns, signIn() must do an INTERACTIVE login (not probe again).
            //
            // "/" when no original URI survives — never fall through to the current
            // URL, which is the failed callback and would dead-end the flow.
            startRegionResolution(originalUri || "/");
            return;
        }
        // Already went through the broker once and STILL no session. Landing on "/"
        // would not terminate this: the pending region and probe marker are still set,
        // so RequireAuth would immediately start another interactive sign-in, fail the
        // same way, and come straight back here — forever. Clear the flow state and
        // stop on a terminal page instead.
        clearLoginFlowMarkers();
        window.location.assign("/unauthorized-app");
        return;
    }
    // Any other callback error (e.g. user not assigned to the app) -> do NOT loop.
    // Clear EVERY marker, not just the probe/broker pair: leaving the pending issuer
    // and client behind would keep hasActiveRegion() true, so a later Home link for a
    // different-region account would skip the bootstrap and sign in against this
    // attempt's Okta org.
    clearLoginFlowMarkers();
    window.location.assign("/unauthorized-app");
}

/**
 * Silently resolve a region from a login_hint (the email Home appends to the product
 * tile link). Returns the region issuer, or null if the broker cannot resolve it.
 *
 * The broker answers with { region, issuer } and deliberately no client id — AC maps the
 * issuer to its OWN SPA client via clientIdForIssuer.
 */
export async function resolveRegionFromLoginHint(loginHint: string): Promise<string | null> {
    // Deadline, because App renders a spinner while this is in flight and nothing else
    // can recover it: a stalled /resolve would never reach the reload or the fallback,
    // leaving the user on the loading screen indefinitely. On timeout we return null
    // like any other failure, which drops the hint and falls through to the broker's
    // email page.
    const controller = new AbortController();
    const deadline = setTimeout(() => controller.abort(), BROKER_RESOLVE_TIMEOUT_MS);
    try {
        const response = await fetch(brokerResolveUrl(loginHint), { signal: controller.signal });
        if (!response.ok) return null;
        const data: { issuer?: string } = await response.json();
        return data?.issuer ?? null;
    } catch {
        return null;
    } finally {
        clearTimeout(deadline);
    }
}

/**
 * The issuer + client OktaAuth is constructed with.
 *
 * When `euEnabled` is false this returns the default pair unconditionally — even if a
 * region token is sitting in storage — so the flag-off path is byte-for-byte what it was
 * before direct-region auth existed.
 */
export function resolveOktaConfig(euEnabled: boolean): OidcTarget {
    const active = euEnabled ? getActiveOidcConfig() : null;
    return {
        issuer: (active?.issuer ?? config.ISSUER) as string,
        clientId: (active?.clientId ?? config.CLIENT_ID) as string
    };
}
